/**
 * Base RTK Query API Slice
 * Handles all API configuration, authentication, and token refresh
 * Feature-specific endpoints are injected into this base slice
 */

import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
  BaseQueryFn,
  FetchArgs,
} from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../utils/config";
import { logout as logoutAction } from "../../store/slices/authSlice";

/**
 * Base query configuration with credentials for HttpOnly cookies
 * Cookies are automatically sent with every request
 * No localStorage tokens needed - backend handles auth via secure HttpOnly cookies
 */
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: "include", // Include HttpOnly cookies in all requests (cross-origin)
  prepareHeaders: (headers) => {
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

/**
 * Enhanced base query with automatic token refresh on 401/403 responses
 * Implements the token refresh pattern:
 * 1. On 401/403, attempt to refresh the access token (except for login endpoint)
 * 2. If refresh succeeds, retry the original request
 * 3. If refresh fails, logout user
 */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Execute the initial request
  let result = await baseQuery(args, api, extraOptions);

  // Get the endpoint URL
  const endpoint = typeof args === "string" ? args : args.url;

  // Handle 401 Unauthorized or 403 Forbidden - attempt token refresh
  // BUT skip refresh retry for login endpoint (failed credentials should not trigger refresh)
  if (
    result.error &&
    (result.error.status === 401 || result.error.status === 403) &&
    endpoint !== "/api/login" // Don't retry login with refresh token
  ) {
    console.log("Token expired or unauthorized, attempting refresh...");

    // Attempt to refresh the access token
    // RefreshToken cookie is automatically sent by browser
    const refreshResult = await baseQuery(
      {
        url: "/api/refresh",
        method: "POST",
        body: { clientType: "web" },
      },
      api,
      extraOptions
    );

    if (refreshResult.data) {
      // Token refresh successful - new HttpOnly cookies set by backend automatically
      console.log("Token refresh successful, retrying original request");

      // Retry the original request with new token cookies
      result = await baseQuery(args, api, extraOptions);
    } else {
      // Token refresh failed - logout user with notification
      console.error("Token refresh failed, logging out");

      // Show user-friendly message before logout
      const message =
        refreshResult.error?.status === 401
          ? "Your session has expired. Please log in again to continue."
          : "Authentication failed. Please log in again.";

      // Dispatch custom event to show toast notification
      window.dispatchEvent(
        new CustomEvent("showLogoutReason", {
          detail: { message },
        })
      );

      // Small delay to ensure toast is visible before logout redirect
      setTimeout(() => {
        api.dispatch(logoutAction());
      }, 100);
    }
  }

  // Log errors for monitoring
  if (result.error) {
    console.error("API Error:", {
      endpoint: typeof args === "string" ? args : args.url,
      status: result.error.status,
      data: result.error.data,
    });
  }

  return result;
};

/**
 * Base API slice with RTK Query
 * All feature-specific endpoints will be injected into this slice
 */
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,

  // Keep cached data for 60 seconds after component unmount
  keepUnusedDataFor: 60,

  // Define cache tags for invalidation
  tagTypes: [
    "Auth",
    "User",
    "Messages",
    "Payments",
    "Analytics",
    "Tickets",
    "Offers",
    "Malls",
    "Stores",
    "Events",
  ],

  // Empty endpoints - feature slices will inject their endpoints
  endpoints: () => ({}),

  // Refetch on window focus
  refetchOnFocus: true,

  // Refetch on network reconnection
  refetchOnReconnect: true,

  // Refetch on mount if data is stale
  refetchOnMountOrArgChange: 30, // 30 seconds
});
